import type { SorterResult } from "antd/es/table/interface";
import type { FC } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { AddPhysicalButton } from "src/app/features/Contract/components/buttons/AddPhysicalButton";
import { AddVirtualButton } from "src/app/features/Contract/components/buttons/AddVirtualButton";
import type { SiteContractEntity } from "src/app/features/Site/types/SiteContractEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { canAccess } from "../../../../core/router/auth/permissions";
import { Permissions } from "../../../../core/router/config/Permissions";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { withReturnTo } from "../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { type ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import {
    displayDateTimeValue,
    displayDateValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { TableSearchBar } from "../../../Table/components/TableSearchBar/TableSearchBar";
import { useTableSearchFilter } from "../../../Table/hooks/useTableSearchFilter";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";

export interface SitesContractsTableProps extends AntCustomFormField<SiteContractEntity[]>, CommonFieldAttributes {}

const SEARCHABLE_FIELDS: (keyof SiteContractEntity)[] = [
    "id",
    "contractNumber",
    "acquirer",
    "appProfile",
    "contractProfile",
    "terminalType",
    "contractCardType",
    "validFrom",
    "validTo",
    "changed",
];

const CONTRACT_PROFILE_COLUMN_DATA_GROUP_TYPES = new Set(["APAC", "IPASO", "MADIC"]);

export const SitesContractsTable: FC<SitesContractsTableProps> = ({ value = [], onChange, readOnly }) => {
    const { t } = useTranslation();
    const { filter, setFilter, filteredItems: filteredContracts } = useTableSearchFilter(value || [], SEARCHABLE_FIELDS);
    const formConfigName = getAssignedDataGroup()?.formConfigName?.toUpperCase();
    const showContractProfileColumn = Boolean(formConfigName && CONTRACT_PROFILE_COLUMN_DATA_GROUP_TYPES.has(formConfigName));
    const getColumnsConfig = useCallback(
        (_t: Function, sortInfo: SorterResult<SiteContractEntity>): ExtendedColumnsType<SiteContractEntity>[] => {
            return [
                {
                    title: t("contract.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractNumber"),
                    key: "contractNumber",
                    dataIndex: "contractNumber",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "contractNumber" ? sortInfo.order : null,
                },
                {
                    title: t("genericTable.column.status"),
                    key: "active",
                    dataIndex: "active",
                    render: (cellValue) => displayStatusValue(cellValue),
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "active" ? sortInfo.order : null,
                },
                {
                    title: t("contract.acquirer"),
                    key: "acquirer",
                    dataIndex: "acquirer",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "acquirer" ? sortInfo.order : null,
                },
                {
                    title: t("contract.appProfile"),
                    key: "appProfile",
                    dataIndex: "appProfile",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "appProfile" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractProfile"),
                    key: "contractProfile",
                    dataIndex: "contractProfile",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "contractProfile" ? sortInfo.order : null,
                    hidden: !showContractProfileColumn,
                },
                {
                    title: t("contract.terminalType"),
                    key: "terminalType",
                    dataIndex: "terminalType",
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "terminalType" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractCardType"),
                    key: "contractCardType",
                    dataIndex: "contractCardType",
                    render: (cellValue) => displayEnumValue(cellValue, "contract-card-type"),
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "contractCardType" ? sortInfo.order : null,
                },
                {
                    title: t("contract.validFrom"),
                    key: "validFrom",
                    dataIndex: "validFrom",
                    render: (cellValue) => displayDateValue(cellValue),
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "validFrom" ? sortInfo.order : null,
                },
                {
                    title: t("contract.validTo"),
                    key: "validTo",
                    dataIndex: "validTo",
                    render: (cellValue) => displayDateValue(cellValue),
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "validTo" ? sortInfo.order : null,
                },
                {
                    title: t("contract.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    render: (cellValue) => displayDateTimeValue(cellValue),
                    sorter: true,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                },
            ];
        },
        [t, showContractProfileColumn],
    );

    const navigate = useNavigate();
    const { id: siteId } = useParams();

    const onDataChanged = (data: SiteContractEntity[]) => {
        if (onChange) {
            const visibleIds = new Set(filteredContracts.map((contract) => contract.id));
            const newIds = new Set(data.map((contract) => contract.id));
            const removedIds = new Set([...visibleIds].filter((id) => !newIds.has(id)));
            const addedContracts = data.filter((contract) => !visibleIds.has(contract.id));
            onChange([...(value || []).filter((contract) => !removedIds.has(contract.id)), ...addedContracts]);
        }
    };
    const onRowClick = (record: SiteContractEntity) => {
        const type = record.physical ? "physical" : "virtual";
        const route = `${BASE_PATH}/${ResourceName.CONTRACTS}/${type}/${FORM_ACTION.EDIT}/${
            record.id
        }?discriminator=${type.toUpperCase()}&service=true&id=${record.id}`;

        navigate(route, withReturnTo(location));
    };

    return (
        <div data-name="sites_contracts_table">
            <TableSearchBar value={filter} onChange={setFilter} />
            <PropertyTable<SiteContractEntity>
                name="sites_contracts_table"
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                tableData={filteredContracts}
                readOnly={readOnly || false}
                onDataChanged={onDataChanged}
                customActions={[
                    <AddPhysicalButton key="addPhysicalContract" siteId={siteId} />,
                    ...(canAccess(ResourceName.CONTRACTS, Permissions.VIRTUAL)
                        ? [<AddVirtualButton key="addVirtualContract" siteId={siteId} />]
                        : []),
                ]}
                onRow={(record: SiteContractEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
            />
        </div>
    );
};
